<script setup lang="ts">
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _unused = t

interface Props {
  visible: boolean
  title: string
  icon?: string
  fullscreen?: boolean  // true：EQ/吃鸡全覆盖，false：降噪/AI普通弹窗
  hideHeader?: boolean   // true：隐藏标题栏（如降噪面板）
}

const props = withDefaults(defineProps<Props>(), {
  fullscreen: false,
  hideHeader: false
})
const emit = defineEmits<{
  (e: 'close'): void
}>()

const closePopup = () => {
  emit('close')
}
</script>

<template>
  <!-- 全屏模式：覆盖整个右侧 -->
  <div v-if="visible && fullscreen" class="nav-popup-overlay" @click="closePopup">
    <div class="nav-popup fullscreen" @click.stop>
      <!-- 弹窗头部 -->
      <div class="popup-header">
        <div class="popup-title">
          <img v-if="icon" :src="icon" class="popup-icon" alt="">
          <span>{{ title }}</span>
        </div>
        <button class="close-btn" @click="closePopup">
          <el-icon><Close /></el-icon>
        </button>
      </div>
      <!-- 弹窗内容 -->
      <div class="popup-content">
        <slot></slot>
      </div>
    </div>
  </div>

  <!-- 固定尺寸模式：不覆盖右侧内容（降噪/AI） -->
  <Transition name="nav-popup-slide">
    <div v-if="visible && !fullscreen" class="nav-popup-fixed" @click.stop>
      <slot></slot>
    </div>
  </Transition>
</template>

<style scoped lang="scss">
// 全屏模式样式
.nav-popup-overlay {
  position: fixed;
  top: 0;
  left: 80px;
  right: 0;
  bottom: 0;
  background: #1a1a1f;
  z-index: 200;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
}

.nav-popup {
  width: 380px;
  height: 100%;
  background: linear-gradient(180deg, #1a1a1f 0%, #0f0f12 100%);
  border-right: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s ease-out;

  &.fullscreen {
    width: 100%;
  }
}

// 固定尺寸模式（降噪/AI）- 不覆盖右侧内容
.nav-popup-fixed {
  position: fixed;
  top: 12%;
  left: 76px;
  z-index: 201;
  transform: translateX(0);
}

@keyframes slideIn {
  from {
    transform: translateX(-100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

// 使用 Vue 的离场过渡，等滑出动画完成后再移除弹窗。
// -14px 将弹窗右边缘收进 62px 侧边栏后方（弹窗 left 为 76px）。
.nav-popup-slide-enter-active,
.nav-popup-slide-leave-active {
  transition: transform 0.3s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.nav-popup-slide-enter-from,
.nav-popup-slide-leave-to {
  transform: translateX(calc(-100% - 14px));
}

.nav-popup-slide-leave-active {
  pointer-events: none;
}

.popup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.popup-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 18px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);

  .popup-icon {
    width: 24px;
    height: 24px;
  }
}

.close-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.05);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.6);
  transition: all 0.3s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.9);
  }
}

.popup-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }
}
</style>
